import { COUNTRIES, CITIZENSHIP_LABELS, type CountryCode } from './enums';
import { parseInternationalPhoneList } from '../utils/international-phone';

/** Страны колонок телефона; OTHER остаётся для полного международного номера. */
export type CandidatePhoneCountry = CountryCode | 'OTHER';
type PhoneSuffix<C extends CandidatePhoneCountry> = Capitalize<Lowercase<C>>;
export type PhoneRawField<C extends CandidatePhoneCountry = CandidatePhoneCountry> = `phone${PhoneSuffix<C>}Raw`;
export type PhoneNumbersField<C extends CandidatePhoneCountry = CandidatePhoneCountry> = `phones${PhoneSuffix<C>}`;
export type PhoneQueryField<C extends CandidatePhoneCountry = CandidatePhoneCountry> = `phone${PhoneSuffix<C>}`;
export type PhoneRawColumn<C extends CandidatePhoneCountry = CandidatePhoneCountry> = `phone_${Lowercase<C>}_raw`;
export type PhoneNumbersColumn<C extends CandidatePhoneCountry = CandidatePhoneCountry> = `phones_${Lowercase<C>}`;
export type PhoneMatchField<C extends CandidatePhoneCountry = CandidatePhoneCountry> = `phone_${Lowercase<C>}`;

export interface PhoneFieldDescriptor<C extends CandidatePhoneCountry = CandidatePhoneCountry> {
  country: C;
  rawField: PhoneRawField<C>;
  numbersField: PhoneNumbersField<C>;
  rawColumn: PhoneRawColumn<C>;
  numbersColumn: PhoneNumbersColumn<C>;
  label: string;
  visibleByDefault: boolean;
}

function describePhone<C extends CandidatePhoneCountry>(country: C): PhoneFieldDescriptor<C> {
  const lower = country.toLowerCase();
  const suffix = lower[0]!.toUpperCase() + lower.slice(1);
  return {
    country,
    rawField: `phone${suffix}Raw` as PhoneRawField<C>,
    numbersField: `phones${suffix}` as PhoneNumbersField<C>,
    rawColumn: `phone_${lower}_raw` as PhoneRawColumn<C>,
    numbersColumn: `phones_${lower}` as PhoneNumbersColumn<C>,
    label: country === 'OTHER' ? 'Телефон других стран' : `Телефон ${CITIZENSHIP_LABELS[country]}`,
    visibleByDefault: country === 'BY' || country === 'RU',
  };
}

/** Добавление страны в COUNTRIES автоматически добавляет её телефонное поле. */
export const COUNTRY_PHONE_FIELDS: readonly PhoneFieldDescriptor<CountryCode>[] = COUNTRIES.map(describePhone);
export const PHONE_FIELDS: readonly PhoneFieldDescriptor[] = [
  ...COUNTRY_PHONE_FIELDS,
  describePhone('OTHER'),
];

export const PHONE_RAW_MAX_LENGTH = 500;

/**
 * Канонизация добавляет национальному номеру код страны и плюс. Поэтому
 * предел объединённого поля нельзя считать суммой длин исходных полей.
 * Консервативная граница: каждый исходный символ может породить не более
 * одного номера E.164 (15 цифр), плюса и разделителя — всего 17 символов.
 * Неизвестная пометка сохраняется без расширения и также укладывается.
 */
export const PHONE_OTHER_RAW_MAX_LENGTH = PHONE_FIELDS.filter(
  ({ country }) => country !== 'BY' && country !== 'RU',
).length * PHONE_RAW_MAX_LENGTH * 17;

export function phoneFieldForKey(key: string): PhoneFieldDescriptor | undefined {
  return PHONE_FIELDS.find((field) => field.rawField === key || field.numbersField === key);
}

export type CandidatePhoneInput = Partial<Record<PhoneRawField, string | null | undefined>>;

/** Все поля формы присутствуют, даже если новый столбец ещё не заполнен. */
export function candidatePhoneValues(row: CandidatePhoneInput): Record<PhoneRawField, string> {
  return Object.fromEntries(PHONE_FIELDS.map(({ rawField }) => [rawField, row[rawField] ?? ''])) as Record<PhoneRawField, string>;
}

/** В ЧС отдельны только РБ и РФ; остальные поля складываются в дополнительный номер. */
export function otherCountryPhonesRaw(row: CandidatePhoneInput): string {
  const seen = new Set<string>();
  return PHONE_FIELDS.filter(({ country }) => country !== 'BY' && country !== 'RU')
    .flatMap(({ rawField, country }) => parseInternationalPhoneList(row[rawField], country))
    .flatMap(({ raw, normalized }) => {
      const value = normalized ? `+${normalized.e164}` : raw;
      if (seen.has(value)) return [];
      seen.add(value);
      return [value];
    }).join('\n');
}
